import React, { ReactNode } from 'react';
import { AuthProvider as OidcAuthProvider, useAuth as useOidcAuth, AuthProviderProps as OidcAuthProviderProps } from 'react-oidc-context';
import { User } from 'oidc-client-ts';
import oidcConfig, { buildCognitoLogoutUrl } from '../oidc-config.ts';

interface CustomUser {
  username: string;
  userId: string;
  groups?: string[];
  email?: string;
}

interface AuthContextType {
  user: CustomUser | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  oidcUser: User | null;
}

export const useAuth = (): AuthContextType => {
  const oidcAuth = useOidcAuth();
  
  const getCustomUser = (oidcUser: User | null): CustomUser | null => {
    if (!oidcUser) return null;
    
    const profile = oidcUser.profile;
    const groups = profile['cognito:groups'] as string[] || [];
    
    return {
      username: profile.preferred_username || profile.sub || 'unknown',
      userId: profile.sub || '',
      groups,
      email: profile.email
    };
  };

  const customUser = getCustomUser(oidcAuth.user);
  const isAdmin = customUser?.groups?.includes('admin') || customUser?.username === 'admin';

  const customLogout = async () => {
    try {
      // Remove user from OIDC client storage
      await oidcAuth.removeUser();

      // Redirect to Cognito logout endpoint
      const logoutUrl = buildCognitoLogoutUrl();
      window.location.href = logoutUrl;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
      // Fallback: just redirect to home
      window.location.href = '/';
    }
  };

  return {
    user: customUser,
    isAuthenticated: oidcAuth.isAuthenticated,
    isAdmin,
    isLoading: oidcAuth.isLoading,
    login: oidcAuth.signinRedirect,
    logout: customLogout,
    oidcUser: oidcAuth.user
  };
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const oidcProviderProps: OidcAuthProviderProps = {
    ...oidcConfig,
    children,
    onSigninCallback: () => {
      // Handle successful sign-in
      window.history.replaceState({}, document.title, window.location.pathname);
    },
    onRemoveUser: () => {
      // Handle user removal
      if (process.env.NODE_ENV === 'development') {
        console.log('User removed');
      }
    }
  };

  return (
    <OidcAuthProvider {...oidcProviderProps}>
      {children}
    </OidcAuthProvider>
  );
};